import { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import CallSplitIcon from '@mui/icons-material/CallSplit';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { UrlParams } from 'common/types';
import { extractFacetExpression } from 'common/util/graph';
import { useSetSearchParams } from 'hooks/useSetSearchParams';

import ComboBox from 'components/common/ComboBox/ComboBox';
import { searchParamRecordState } from 'components/common/Managers/RouteStateManager.state';

import './BreakdownSelect.scss';

type BreakdownSelectProps = {
  facetList: Array<string>;
};

/**
 * This component allows the user to set a breakdown facet via a combo box (input plus a dropdown list).
 *
 * @prop facetList   list of top facet names
 */
function BreakdownSelect({
  facetList,
}: BreakdownSelectProps): JSX.Element {

  const setSearchParams = useSetSearchParams();
  const searchParams = useRecoilValue(searchParamRecordState);
  const [ input, setInput ] = useState(searchParams.breakdownFacet || '');

  useEffect(() => {
    setInput(searchParams.breakdownFacet || '');
  }, [ searchParams.breakdownFacet ]);

  const onInputChange = useCallback((facet) => setInput(facet), [ setInput ]);

  const onInputClear = (): void => {
    setSearchParams({
      breakdownFacet: '',
      showGraphs:     '',
      hideGraphs:     '',
    } as UrlParams);
  };

  const onSelect = (item: string): void => {
    setSearchParams({
      breakdownFacet: item,
      showGraphs:     '',
      hideGraphs:     '',
    } as UrlParams);
  };

  const serverHostSection = facetList.includes('serverHost') ? ['serverHost'] : [];

  // user has typed text into the input and pressed Enter
  const onEnter = (item: string): void => {
    // do not allow user to enter an invalid breakdown facet
    if (!!item && !facetList.includes(item) && !serverHostSection.includes(item)) {
      return;
    }

    const newSearchParams = { breakdownFacet: item } as UrlParams;

    // if we are resetting to no breakdownFacet, make sure to strip the facet function
    if (!item) {
      const facetExp = extractFacetExpression(searchParams.facet || '');
      newSearchParams.facet = facetExp.facetVariable || '';
    }
    setSearchParams(newSearchParams);
  };


  return (
    <div className="BreakdownSelect">
      <div className="BreakdownSelect__IconLabel">
        <CallSplitIcon fontSize="small" />
      </div>
      <ComboBox
        className="BreakdownSelect__Input"
        placeholder="Break Down"
        value={input}
        sections={[serverHostSection, facetList]}
        limit={K.MAX_SEARCH_FIELD_ITEMS}
        onChange={onInputChange}
        onClear={onInputClear}
        onSelect={(value: string): void => onSelect(value)}
        onEnter={(value: string): void => onEnter(value)}
        size="sm"
        noResultsMsg="No results found"
        testId={ELEMENT.searchGraphBreakdown}
        selectOnEnter
      />
    </div>
  );
}

export default BreakdownSelect;
